import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';

import { Product } from '../../common/constants/event-attributes';
import { type Team } from '../../common/constants/team';
import { createProductSpecificAnalyticsIntegrationPayload } from '../../services/create-product-specific-analytics-integration-payload';
import type { AnalyticsEventPayloadForProduct } from '../../services/create-product-specific-analytics-integration-payload/types';

interface JiraCreateAnalyticsEventParams {
	action: string;
	actionSubject: string;
	source: string;
	actionSubjectId: string;
	tags: string[];
}

interface JiraFireEventParams {
	eventName: string;
	attributes: Record<string, any>;
}

/**
 * Converts the standard event payload to the format required by Jira's createAnalyticsEvent function
 *
 * @param payload The standard analytics event payload
 * @returns Parameters for Jira's createAnalyticsEvent function
 */
export const convertEventPayloadForJiraCreateAnalyticsEvent = (
	payload: AnalyticsEventPayloadForProduct,
): JiraCreateAnalyticsEventParams => {
	const { data } = payload;

	return {
		action: data.action,
		actionSubject: data.actionSubject,
		source: data.source,
		actionSubjectId: data.actionSubjectId,
		tags: data.tags,
	};
};

/**
 * Extracts the eventName and attributes needed for Jira's fireUIAnalytics function
 *
 * @param payload The standard analytics event payload
 * @returns Parameters for Jira's fireUIAnalytics function
 */
export const convertEventAttributesForJiraFireEvent = (
	payload: AnalyticsEventPayloadForProduct,
): JiraFireEventParams => {
	const { data } = payload;
	const eventName = `${data.actionSubject} ${data.action}`;

	return {
		eventName,
		attributes: data.attributes,
	};
};

/**
 * Converts an analytics event from the analytics listener into Jira-specific format.
 * This is used by the GrowthErrorEventAnalyticsListener when integrated with Jira products.
 *
 * The function extracts relevant attributes from the UIAnalyticsEvent payload,
 * creates a product-specific analytics payload for Jira, and converts it into
 * the correct formats for both Jira's createAnalyticsEvent and fireEvent functions.
 *
 * @param event The UIAnalyticsEvent from the analytics listener
 * @returns Object containing both createEventPayload and fireEventPayload for Jira integration
 */
export const convertEventPayloadForJiraAnalyticsListener = (event: UIAnalyticsEvent) => {
	const { payload } = event;

	// Extract attributes from the event payload and create a formatted payload
	// specifically for Jira product integration
	const analyticsEventPayload = createProductSpecificAnalyticsIntegrationPayload({
		product: Product.JIRA,
		team: payload.attributes.team as Team,
		experimentMeta: {
			experimentKey: payload.attributes.experimentKey,
			cohort: payload.attributes.cohort,
		},
		errorType: payload.attributes.errorType,
		errorMessage: payload.attributes.errorMessage,
		source: payload.attributes.source,
		severity: payload.attributes.severity,
	});

	// Return both payload formats needed for complete Jira analytics integration:
	// 1. createEventPayload: Used with Jira's createAnalyticsEvent
	// 2. fireEventPayload: Used with Jira's fireEvent
	return {
		createEventPayload: convertEventPayloadForJiraCreateAnalyticsEvent(analyticsEventPayload),
		fireEventPayload: convertEventAttributesForJiraFireEvent(analyticsEventPayload),
	};
};
